import { WorkspaceFolder } from 'vscode-languageserver-protocol';
import { URI, Utils } from 'vscode-uri';
import * as git from 'isomorphic-git';
import debounce from 'lodash/debounce';
import { DebouncedFunc } from 'lodash';
import { FileResolver } from '../fs';
import { FsClient } from '../fs/fs';
import { GitIgnoreManager } from './git_ignore_manager';

type RepoFileUri = URI;
type RepositoryUri = URI;

export type RepositoryFile = {
  uri: RepoFileUri;
  repositoryUri: RepositoryUri;
  isIgnored: boolean;
  workspaceFolder: WorkspaceFolder;
};

export type GetFileOptions = {
  excludeGitFolder?: boolean;
  excludeIgnored?: boolean;
};

export type GitFiles = {
  ignoreFiles: Map<URI, string>;
  excludeFile: URI | undefined;
  trackedFiles: Set<string>;
};

/**
 * Repository represents a git repository.
 * It is responsible for managing the files in the repository
 * and the `GitIgnoreManager` to manage the gitignore files.
 *
 * It's also used for common git parsing operations, eg
 * `Repository.isGitIgnoreFile`, `Repository.isGitConfigFile`, etc.
 *
 * This class is stateful.
 *
 * This is an internal class of the `RepositoryService`.
 * @see `RepositoryService`
 * @see `GitIgnoreManager`
 */
export class Repository {
  #fileResolver: FileResolver;

  readonly workspaceFolder: WorkspaceFolder;

  /**
   * The URI of the repository root directory.
   * Ex: file:///gitlab/work/git/my-repo
   */
  readonly uri: URI;

  /**
   * The URI of the repository git config file.
   * Ex: file:///gitlab/work/git/my-repo/.git/config
   */
  readonly configFileUri: URI;

  #files: Map<string, RepositoryFile>;

  #ignoreManager?: GitIgnoreManager;

  #fsClient: FsClient;

  #gitFiles: GitFiles;

  #refreshTrackedFilesDebounced: DebouncedFunc<() => Promise<void>>;

  constructor({
    fileResolver,
    configFileUri,
    workspaceFolder,
    uri,
    fsClient,
  }: {
    fileResolver: FileResolver;
    workspaceFolder: WorkspaceFolder;
    uri: RepositoryUri;
    configFileUri: URI;
    fsClient: FsClient;
  }) {
    this.#fileResolver = fileResolver;
    this.workspaceFolder = workspaceFolder;
    this.uri = uri;
    this.configFileUri = configFileUri;
    this.#files = new Map();
    this.#gitFiles = {
      ignoreFiles: new Map(),
      excludeFile: undefined,
      trackedFiles: new Set(),
    };
    this.#fsClient = fsClient;
    this.#refreshTrackedFilesDebounced = debounce(this.#refreshTrackedFilesImpl, 300);
  }

  /**
   * Check if the file is ignored by git.
   * This will match the file against gitignore patterns in the directory structure
   * and all its parent directories.
   *
   * This also checks the .gitignore/exclude file in the root of the git repository.
   *
   * If the file is tracked by git, it is not ignored.
   */
  isFileIgnored(filePath: URI): boolean {
    if (this.#gitFiles.trackedFiles.has(filePath.toString())) {
      return false;
    }
    return this.#ignoreManager?.isIgnored(filePath) ?? false;
  }

  setFile(fileUri: URI): void {
    const isIgnored = this.isFileIgnored(fileUri);
    this.#files.set(fileUri.toString(), {
      uri: fileUri,
      isIgnored,
      repositoryUri: this.uri,
      workspaceFolder: this.workspaceFolder,
    });
  }

  getFile(fileUri: URI, options: GetFileOptions = {}): RepositoryFile | undefined {
    const file = this.#files.get(fileUri.toString());
    if (file && this.#fileFilter(file, options)) {
      return file;
    }
    return undefined;
  }

  removeFile(fileUri: URI): void {
    this.#files.delete(fileUri.toString());
  }

  getCurrentTreeFiles(options: GetFileOptions = {}): RepositoryFile[] {
    return Array.from(this.#files.values()).filter((file) => this.#fileFilter(file, options));
  }

  getCurrentTrackedFiles(): string[] {
    return Array.from(this.#gitFiles.trackedFiles);
  }

  #fileFilter(file: RepositoryFile, options: GetFileOptions): boolean {
    if (options.excludeGitFolder && Repository.isInGitFolder(file.uri)) {
      return false;
    }
    if (this.#gitFiles.trackedFiles.has(file.uri.toString())) {
      return true;
    }
    if (options.excludeIgnored && file.isIgnored) {
      return false;
    }
    return true;
  }

  /**
   * Setup the git repository by creating a new `GitIgnoreManager` and adding the gitignore files.
   *
   * This will also update the tracked files in the repository.
   */
  async setupGitForRepository(files: URI[]): Promise<GitFiles | Error> {
    try {
      const gitFiles = await this.#getGitFiles(files);
      const ignoreManager = new GitIgnoreManager(this.uri);
      for (const [ignoreFile, ignoreFileContent] of gitFiles.ignoreFiles.entries()) {
        ignoreManager.addGitignore(ignoreFile, ignoreFileContent);
      }
      if (gitFiles.excludeFile) {
        ignoreManager.addExcludeFile(
          await this.#fileResolver.readFile({ fileUri: gitFiles.excludeFile }),
        );
      }
      this.#ignoreManager = ignoreManager;
      this.#gitFiles = gitFiles;
      return gitFiles;
    } catch (error) {
      return error as Error;
    }
  }

  async #getGitFiles(files: URI[]): Promise<GitFiles> {
    const ignoreFiles: URI[] = [];
    let excludeFile: URI | undefined;
    for (const file of files) {
      if (Repository.isGitIgnoreFile(file)) {
        ignoreFiles.push(file);
      } else if (Repository.isGitExcludeFile(file)) {
        excludeFile = file;
      }
    }
    const [ignoreContents, trackedFiles] = await Promise.all([
      Promise.all(ignoreFiles.map((fileUri) => this.#fileResolver.readFile({ fileUri }))),
      this.#getTrackedFiles(),
    ]);
    const ignoreFilesMap = new Map(ignoreFiles.map((file, index) => [file, ignoreContents[index]]));

    return {
      ignoreFiles: ignoreFilesMap,
      excludeFile,
      trackedFiles: new Set(trackedFiles),
    };
  }

  async #getTrackedFiles(): Promise<string[]> {
    const trackedFiles = await git.listFiles({
      dir: this.uri.fsPath,
      fs: this.#fsClient,
    });
    return trackedFiles.map((file) => Utils.resolvePath(this.uri, file).toString());
  }

  #updateTrackedFiles(trackedFiles: string[]): void {
    this.#gitFiles.trackedFiles.clear();
    for (const file of trackedFiles) {
      this.#gitFiles.trackedFiles.add(file);
    }
  }

  async #refreshTrackedFilesImpl() {
    const trackedFiles = await this.#getTrackedFiles();
    this.#updateTrackedFiles(trackedFiles);
  }

  /**
   * Refresh the tracked files in the repository.
   * This is debounced to prevent excessive calls to the git command.
   */
  refreshTrackedFiles() {
    return this.#refreshTrackedFilesDebounced();
  }

  dispose(): void {
    this.#ignoreManager?.dispose();
    this.#files.clear();
  }

  static getRepoRootFromGitConfig(gitConfigUri: URI): URI {
    return Utils.resolvePath(gitConfigUri, '..', '..');
  }

  static getRepoGitConfigFile(repoUri: URI): URI {
    return Utils.resolvePath(repoUri, '.git', 'config');
  }

  static getGitConfigFiles(files: URI[]): URI[] {
    return files.filter((file) => Repository.isGitConfigFile(file));
  }

  static isGitIgnoreFile(file: URI): boolean {
    return file.toString().endsWith('.gitignore');
  }

  static isGitConfigFile(file: URI): boolean {
    const filePath = file.toString();
    return filePath.endsWith('/.git/config') || filePath.endsWith('\\.git\\config');
  }

  static isInGitFolder(fileUri: URI): boolean {
    return fileUri.toString().includes('/.git/') || fileUri.toString().includes('\\.git\\');
  }

  static isGitExcludeFile(file: URI): boolean {
    const path = file.toString();
    return path.includes('/.git/info/exclude') || path.includes('\\.git\\info\\exclude');
  }
}
